import type { ApiErrorBody, ApiErrorCode } from '@shared/api-types'

/** Every non-2xx response becomes one of these. */
export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly correlationId: string | null
  readonly fields: Record<string, string> | undefined
  readonly permissions: string[] | undefined
  readonly userCount: number | undefined
  readonly retryAfterSeconds: number | undefined

  constructor(status: number, body: ApiErrorBody, correlationId: string | null) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.correlationId = body.correlation_id ?? correlationId
    this.fields = body.fields
    this.permissions = body.permissions
    this.userCount = body.user_count
    this.retryAfterSeconds = body.retry_after_seconds
  }

  /** Build from a Response. */
  static async fromResponse(res: Response): Promise<ApiError> {
    const correlationId = res.headers.get('X-Request-Id')
    let body: ApiErrorBody

    try {
      const parsed: unknown = await res.json()
      body =
        isErrorEnvelope(parsed)
          ? parsed.error
          : { code: fallbackCode(res.status), message: fallbackMessage(res.status) }
    } catch {
      body = { code: fallbackCode(res.status), message: fallbackMessage(res.status) }
    }

    return new ApiError(res.status, body, correlationId)
  }

  /** The network never answered — a different problem from any HTTP status. */
  static offline(): ApiError {
    return new ApiError(
      0,
      { code: 'NETWORK_ERROR', message: 'Cannot reach the server. Check your connection.' },
      null,
    )
  }

  /** Session gone. The one code the whole app reacts to globally. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Signed in, lacks the permission. Never a redirect — */
  get isForbidden(): boolean {
    return this.status === 403 && this.code !== 'PASSWORD_CHANGE_REQUIRED'
  }

  /** Reached anything before completing the forced first-sign-in change. */
  get needsPasswordChange(): boolean {
    return this.code === 'PASSWORD_CHANGE_REQUIRED'
  }
}

function isErrorEnvelope(value: unknown): value is { error: ApiErrorBody } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error: unknown }).error === 'object' &&
    (value as { error: object | null }).error !== null
  )
}

function fallbackCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHENTICATED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 429) return 'RATE_LIMITED'
  return 'INTERNAL_ERROR'
}

/** Generic copy, on purpose. */
function fallbackMessage(status: number): string {
  if (status === 401) return 'Your session has expired.'
  if (status === 403) return 'You do not have access to this.'
  if (status === 404) return 'Not found.'
  if (status === 429) return 'Too many requests. Try again shortly.'
  if (status >= 500) return 'Something went wrong on our side.'
  return 'That request could not be completed.'
}
